<template>
  <FullPage v-hotkey="shortcuts" class="organization-home">
    <template #topbar-right>
      <BaseButton
        kind="auxiliary"
        text="Settings"
        @click="goToOrganizationSettings"
      >
        <template #icon>
          <SettingsIcon />
        </template>
      </BaseButton>
      <ButtonMenu :offset="[0, 4]" placement="bottom-end">
        <template #btn>
          <BaseButton kind="auxiliary">
            <template #icon>
              <MoreIcon />
            </template>
          </BaseButton>
        </template>
        <template #content>
          <BaseButton
            class="context-action"
            kind="auxiliary"
            text="Copy link"
            is-expanded
            @click="copyLink"
          >
            <template #icon>
              <CopyLinkIcon />
            </template>
            <template #right>{{ copyLinkShortcutDisplay }}</template>
          </BaseButton>
        </template>
      </ButtonMenu>
    </template>
    <div class="content">
      <div class="organization-title-block">
        <div class="organization-title-row">
          <h2>Welcome to {{ organization?.name || "Organization" }}</h2>
        </div>
        <div v-if="members && members.length > 0" class="members-row">
          <ul class="members-avatars">
            <li
              v-for="member in members"
              :key="member.id"
              v-tippy="{
                content: member.name || member.email,
                placement: 'top',
                animation: 'fade',
                duration: 200,
              }"
              class="members-avatars__item"
              @click="openMember(member, $event)"
            >
              <Avatar
                :personish="{
                  kind: 'attendee',
                  email: member.email,
                  name: member.name,
                }"
                size="small"
                badge-side="hide"
              />
            </li>
          </ul>
          <button
            v-if="canInviteMember"
            v-tippy="{
              content: 'Invite',
              placement: 'top',
              animation: 'fade',
              duration: 200,
            }"
            class="invite-btn"
            @click="goToPeople"
          >
            <PlusIcon />
          </button>
        </div>
        <p v-else class="members-empty">No members yet.</p>
      </div>

      <section v-if="assistantWorkspaceId" class="org-assistant-section">
        <div class="section-heading-with-icon">
          <button
            type="button"
            class="section-heading-icon-btn"
            aria-label="Open Assistant"
            @click="openAssistant"
          ></button>
          <h3>Assistant</h3>
        </div>
        <div class="org-assistant-inner">
          <ChatInput
            ref="chatInput"
            :workspace-id="assistantWorkspaceId"
            :reference-search-organization-id="null"
            :reference-context-workspace-id="assistantWorkspaceId"
            :mentions-enabled="mentionsEnabled"
            :attachments="chatAttachments"
            :uploading-files="uploadingFiles"
            :submit-button-state="submitButtonState"
            :error-message="errorMessage"
            @submit="onAssistantSubmit"
            @paste="onPaste"
            @file-input-change="onFileInputChange"
            @drop-files="onDropFiles"
            @remove-attachment="removeAttachment"
          >
            <template #placeholder>
              <AgentPlaceholder :mentions-enabled="mentionsEnabled" />
            </template>
          </ChatInput>
        </div>
      </section>

      <div v-if="workspaces && workspaces.length > 0" class="workspace-list">
        <section class="workspace-section">
          <div class="section-heading-with-icon">
            <h3>Workspaces</h3>
          </div>
          <div class="workspace-items">
            <button
              v-for="ws in workspaces"
              :key="ws.id"
              class="workspace-item"
              @click="goToWorkspace(ws.id)"
            >
              <div class="workspace-initials">
                {{ getInitials(ws.name || "") }}
              </div>
              <span class="workspace-name">{{ ws.name }}</span>
            </button>
          </div>
        </section>
      </div>
      <div v-else class="empty">No workspaces yet.</div>

      <section>
        <div class="section-heading-with-icon">
          <h3>Integrations</h3>
        </div>
        <div v-if="allIntegrations.length > 0" class="integrations-grid">
          <div
            v-for="app in allIntegrations"
            :key="app.id"
            class="integration-card"
            @click="goToIntegrations"
          >
            <div class="integration-card-icon">
              <img v-if="app.logoUri" :src="app.logoUri" class="logo" />
              <IntegrationIcon v-else />
            </div>
            <div class="integration-card-name">{{ app.name }}</div>
            <div class="integration-card-status">
              <span v-if="app.enabled" class="status-dot" />
              <span v-else class="connect-label">Connect</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  </FullPage>
</template>

<script lang="ts">
import type { Integration } from "@membranehq/sdk";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import { computed, defineComponent, ref, toRefs } from "vue";

import type {
  AccountId,
  OrganizationMemberFull,
  OrgId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import AgentPlaceholder from "@/components/ai/AgentPlaceholder.vue";
import ChatInput, { type ChatInputHandle } from "@/components/ai/ChatInput.vue";
import { pendingMessage } from "@/components/ai/pendingMessage";
import { useAttachments } from "@/components/ai/useAttachments";
import BaseButton from "@/components/base/BaseButton.vue";
import schema from "@/components/editor/prosemirror/schema";
import Avatar from "@/components/elements/Avatar.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import CopyLinkIcon from "@/components/icons/CopyLinkIcon.vue";
import IntegrationIcon from "@/components/icons/IntegrationIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import SettingsIcon from "@/components/icons/tabs/SettingsIcon.vue";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";
import { isIntegrationAllowed } from "@/components/settings/integrations/integrationAllowlist";
import {
  assignShortcuts,
  copyLinkItem,
  type Shortcuts,
} from "@/components/shortcuts";
import { MEMBERS_TABLE_ID } from "@/components/tables";
import FullPage from "@/components/templates/FullPage.vue";
import { getOrganizationDefaultWorkspace } from "@/components/workspaces";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { hotkeys } from "@/utils/hotkeys";
import { keymap } from "@/utils/keymap";
import {
  navigateToAgent,
  navigateToAgentConversation,
  navigateToCustomObject,
} from "@/utils/navigate";

/** Prefix assistant messages with org context (personal-assistant chat uses org-scoped workspace). */
function prependOrgHomeAssistantContext(
  doc: ProseMirrorNode,
  orgName: string,
  orgId: OrgId
): ProseMirrorNode {
  const headerText = `You are operating in the organization ${orgName} (${orgId}).
  `;
  const header = schema.nodes.paragraph.create(null, schema.text(headerText));
  const blocks: ProseMirrorNode[] = [header];
  doc.forEach((child) => {
    blocks.push(child);
  });
  return schema.nodes.doc.create(null, blocks);
}

type Workspace = { id: string; name: string };

const ENTITLEMENT_ASSISTANT_KEY = "ai-assistant";

export default defineComponent({
  name: "OrganizationHome",
  components: {
    AgentPlaceholder,
    Avatar,
    BaseButton,
    ButtonMenu,
    ChatInput,
    CopyLinkIcon,
    FullPage,
    IntegrationIcon,
    MoreIcon,
    PlusIcon,
    SettingsIcon,
  },
  props: {
    orgId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { orgId } = toRefs(props);
    const controller = inject.controller();
    const profile = inject.profile();
    const router = inject.router();
    const billing = inject.billing();
    const isModalShown = inject.isModalShown();
    const isInputFocused = inject.isInputFocused();
    const integrationsStore = inject.integrations();

    const releaser = useReleaser();
    const organization = await releaser.add(
      bindAsync(orgId, async (id) =>
        id ? controller.organization.get(id) : releasableRef(null)
      )
    );

    const memberIds = releaser.add(
      bindAsync(orgId, async (id) =>
        id ? controller.organization.member.all(id) : releasableRef([])
      )
    );
    const members = await releaser.add(
      memberIds.then((ids) =>
        bindAsync(ids, async (ids) => {
          if (!ids?.length) return releasableRef<OrganizationMemberFull[]>([]);

          const memberPromises = ids.map(async (memberId: AccountId) => {
            const memberRef = await controller.organization.member.get(
              orgId.value,
              memberId
            );
            const member = memberRef.value;
            memberRef.release();
            return member;
          });

          const loaded = (await Promise.all(memberPromises)).filter(
            (m): m is OrganizationMemberFull => m !== null
          );
          loaded.sort((a, b) => a.name.localeCompare(b.name));
          return releasableRef(loaded);
        })
      )
    );

    const workspaceIds = releaser.add(
      bindAsync(orgId, async (id) => {
        if (!id) return releasableRef<string[]>([]);
        return controller.organization.workspaces.all(id);
      })
    );

    const workspaces = await releaser.add(
      workspaceIds.then((ids) =>
        bindAsync(ids, async (ids) => {
          if (!ids?.length) return releasableRef<Workspace[]>([]);

          const workspacePromises = ids.map(async (workspaceId: string) => {
            const workspaceRef = await controller.workspace.get(workspaceId);
            const workspace = workspaceRef.value;
            workspaceRef.release();
            if (!workspace) return null;
            return { id: workspace.id, name: workspace.name };
          });

          const list = (await Promise.all(workspacePromises))
            .filter((ws: Workspace | null): ws is Workspace => ws !== null)
            .toSorted((a, b) => a.name.localeCompare(b.name));

          return releasableRef(list);
        })
      )
    );

    /** Personal org (assistant CRDT + composer live here), not the viewed org home. */
    const personalOrganizationId = computed(
      () => profile.value?.user.personal.organizationId ?? ""
    );

    // Account-level: any org's plan grants the assistant (it lives in the
    // personal org, which stays Free when a team org is on Business).
    const isEntitledToAssistant = computed(
      () =>
        billing.value?.isEntitledTo(ENTITLEMENT_ASSISTANT_KEY)?.value ?? false
    );

    const assistantWorkspaceId = await releaser.add(
      bindAsync(personalOrganizationId, async (oid) => {
        if (!oid) return releasableRef<WorkspaceId | undefined>(undefined);
        const wsId = await getOrganizationDefaultWorkspace(controller, oid);
        return releasableRef(wsId);
      })
    );

    const chatInput = ref<ChatInputHandle | null>(null);

    const {
      attachments: chatAttachments,
      uploading: uploadingFiles,
      addFiles,
      remove: removeAttachment,
      clear: clearAttachments,
    } = useAttachments(controller, assistantWorkspaceId);

    const onDropFiles = (files: File[]) => {
      if (files.length > 0) void addFiles(files);
    };

    const onFileInputChange = (event: Event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      const input = event.target;
      if (input.files && input.files.length > 0) {
        void addFiles(Array.from(input.files));
        input.value = "";
      }
    };

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        void addFiles(files);
      }
    };

    const mentionsEnabled = computed(() => {
      const ws = assistantWorkspaceId.value;
      if (!ws) return false;
      if (!profile.value?.user.personal.workspaceId) return false;
      return ws !== profile.value.user.personal.workspaceId;
    });
    const isThinking = ref(false);
    const errorMessage = ref<string | null>(null);

    const submitButtonState = computed(() =>
      isThinking.value ? "loading" : "default"
    );

    const onAssistantSubmit = async (proseMirrorDoc: ProseMirrorNode) => {
      if (!proseMirrorDoc || isThinking.value) return;
      if (!assistantWorkspaceId.value) return;

      const personalOrgId = profile.value?.user.personal.organizationId;
      if (!personalOrgId) return;

      if (!isEntitledToAssistant.value) {
        openUpgradeModal(billing, undefined, ENTITLEMENT_ASSISTANT_KEY);
        return;
      }

      chatInput.value?.setValue?.("");

      const orgName = organization.value?.name ?? "Organization";
      const docForSend = prependOrgHomeAssistantContext(
        proseMirrorDoc,
        orgName,
        orgId.value
      );

      const { serializeDoc } = await import("@/components/editor/serialize");
      const serializedText = serializeDoc(docForSend);

      const currentAttachments =
        chatAttachments.value.length > 0
          ? [...chatAttachments.value]
          : undefined;
      clearAttachments();

      isThinking.value = true;
      errorMessage.value = null;
      try {
        const assistantRef = await controller.ai.assistant.get();
        const assistantId = assistantRef.value;
        assistantRef.release();
        if (!assistantId) return;

        const newConvId = await controller.ai.agent.conversation.create(
          personalOrgId,
          assistantId,
          null
        );

        pendingMessage.value = {
          conversationId: newConvId,
          doc: docForSend,
          text: serializedText,
          attachments: currentAttachments,
        };

        await navigateToAgentConversation(router, assistantId, newConvId);
      } catch (err) {
        errorMessage.value =
          err instanceof Error ? err.message : "Unable to reach AI model.";
      } finally {
        isThinking.value = false;
      }
    };

    const getInitials = (name: string) => {
      return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    const organizationId = computed(() => orgId.value);
    const integrations = integrationsStore.forOrganization(organizationId);

    const enabledIntegrations = computed<Integration[]>(() => {
      const ids = [
        ...new Set(
          integrations.connections.value
            .map((c) => c.integrationId)
            .filter((integrationId) => integrationId !== undefined)
        ),
      ];
      return ids
        .map((id) => integrations.getServiceById(id))
        .filter((s): s is Integration => !!s)
        .toSorted((a, b) => a.name.localeCompare(b.name));
    });

    const allIntegrations = computed(() => {
      const enabledIds = new Set(enabledIntegrations.value.map((e) => e.id));
      return integrations.services.value
        .filter((s) => isIntegrationAllowed(s.key))
        .map((s) => ({
          ...s,
          enabled: enabledIds.has(s.id),
        }))
        .toSorted((a, b) => {
          if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
    });

    const goToWorkspace = (workspaceId: string) => {
      router.push({
        name: RouteNames.Workspace,
        params: { orgId: orgId.value, workspaceId },
      });
    };

    const goToIntegrations = () => {
      router.push({
        name: RouteNames.SettingsIntegrations,
        params: { orgId: orgId.value },
      });
    };

    const canInviteMember = computed(
      () => organization.value?.permissions?.includes("member-invite") ?? false
    );

    const goToPeople = () => {
      router.push({
        name: RouteNames.SettingsPeople,
        params: { orgId: orgId.value },
      });
    };

    // Member objects live in a workspace's members database (keyed by the
    // member's account id), not at the organization level: open the member in
    // the first of the organization's workspaces they belong to.
    const openMember = async (
      member: OrganizationMemberFull,
      event?: MouseEvent
    ) => {
      for (const ws of workspaces.value ?? []) {
        const workspaceRef = await controller.workspace.get(ws.id);
        const workspaceMembers = workspaceRef.value?.members;
        workspaceRef.release();
        if (workspaceMembers?.[member.id]) {
          await navigateToCustomObject(
            router,
            ws.id,
            MEMBERS_TABLE_ID,
            member.id,
            event
          );
          return;
        }
      }
    };

    const goToOrganizationSettings = () => {
      router.push({
        name: RouteNames.SettingsOrganization,
        params: { orgId: orgId.value },
      });
    };

    const openAssistant = async () => {
      const personalOrgId = profile.value?.user.personal.organizationId;
      if (!personalOrgId) return;

      if (!isEntitledToAssistant.value) {
        openUpgradeModal(billing, undefined, ENTITLEMENT_ASSISTANT_KEY);
        return;
      }

      const assistantRef = await controller.ai.assistant.get();
      const assistantId = assistantRef.value;
      assistantRef.release();
      if (!assistantId) return;
      await navigateToAgent(router, assistantId);
    };

    const copyLink = async () => {
      const url = `https://open.routine.co?path=/organizations/${orgId.value}`;
      await copy(url);
      controller.notifications.notify("Link copied to clipboard");
    };

    const shortcuts = computed<Shortcuts>(() => ({
      ...assignShortcuts({ isModalShown, isInputFocused }, copyLinkItem, () => {
        void copyLink();
      }),
    }));

    const copyLinkShortcutDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyShift, keymap.keyL],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";

    return {
      canInviteMember,
      goToPeople,
      goToOrganizationSettings,
      openAssistant,
      openMember,
      members,
      organization,
      workspaces,
      getInitials,
      allIntegrations,
      assistantWorkspaceId,
      isEntitledToAssistant,
      chatInput,
      chatAttachments,
      uploadingFiles,
      removeAttachment,
      onPaste,
      onFileInputChange,
      onDropFiles,
      mentionsEnabled,
      submitButtonState,
      errorMessage,
      onAssistantSubmit,
      copyLink,
      copyLinkShortcutDisplay,
      shortcuts,
      goToWorkspace,
      goToIntegrations,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/_variables.scss" as *;
@use "@/styles/context-actions.scss";
@use "../styles/settings.scss";

.organization-home {
  /* Flex sections (from settings.scss) need this so chat input can shrink horizontally */
  section {
    min-width: 0;
  }

  .content {
    overflow-y: auto;
    height: 100%;
    padding-bottom: 96px;
    max-width: $document-wide-max-width;
    margin: 0 auto;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .organization-title-block {
    margin-bottom: 24px;

    .organization-title-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      margin-bottom: 12px;
      flex-wrap: wrap;
    }

    h2 {
      text-align: center;
      margin: 0;
    }

    .organization-settings-btn {
      flex-shrink: 0;
    }
  }
}

.members-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.members-avatars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.members-avatars__item {
  flex-shrink: 0;
  @include transition(opacity 150ms ease);

  &:hover {
    opacity: 0.7;
  }
}

.invite-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: default;
  @include transition(all 150ms ease-out);

  @include themed() {
    background: t($primary);
    color: t($bg);
  }

  :deep(svg) {
    width: 10px;
    height: 10px;

    .stroke {
      stroke: currentColor;
    }
  }

  &:hover {
    opacity: 0.7;
  }
}

.members-empty {
  @extend %font-body-regular;
  margin: 0;
  text-align: center;

  @include themed() {
    color: t($grey-50);
  }
}

.org-assistant-section {
  margin-bottom: 28px;
  width: 100%;
  min-width: 0;
}

.org-assistant-inner {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;

  :deep(.chat-input-container) {
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  :deep(.composer) {
    min-height: 70px;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  :deep(.composer-editor-wrapper) {
    max-width: 100%;
    min-width: 0;
  }

  :deep(.composer .ProseMirror) {
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  :deep(.chat-input-bottom-row) {
    min-width: 0;
    max-width: 100%;
    flex-wrap: wrap;
    row-gap: 8px;
  }

  :deep(.chat-attachments) {
    min-width: 0;
    flex: 1 1 0;
  }

  :deep(.submit-button) {
    flex-shrink: 0;
  }
}

.integrations-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;

  .integration-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 14px 8px 12px;
    border-radius: $border-radius-l;
    @include transition(all 150ms ease);

    @include themed() {
      background: t($grey-03);
      border: 1px solid t($border);
    }

    &:hover {
      @include themed() {
        background: t($grey-05);
        border-color: t($grey-10);
      }

      .connect-label {
        @include themed() {
          color: t($primary);
        }
      }
    }
  }

  .integration-card-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;

    .logo {
      width: 32px;
      height: 32px;
      border-radius: $border-radius-m;
    }
  }

  .integration-card-name {
    @extend %font-caption-medium;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;

    @include themed() {
      color: t($primary);
    }
  }

  .integration-card-status {
    margin-top: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 18px;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: $green;
  }

  .connect-label {
    @extend %font-caption-regular;
    @include transition(color 150ms ease);

    @include themed() {
      color: t($grey-50);
    }
  }
}

.section-heading-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  h3 {
    margin-bottom: 0;
  }

  .section-heading-icon {
    flex-shrink: 0;

    @include themed() {
      color: t($grey-50);
    }
  }

  .section-heading-icon-btn {
    flex-shrink: 0;
    padding: 0;
    margin: 0;
    border: none;
    background: none;
    cursor: pointer;
    border-radius: 50%;
    line-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    @include transition(opacity 150ms ease);

    &:hover {
      opacity: 0.85;
    }

    &:focus-visible {
      outline: 2px solid;
      outline-offset: 2px;

      @include themed() {
        outline-color: t($tertiary-50);
      }
    }

    .section-heading-icon {
      @include themed() {
        color: t($grey-50);
      }
    }
  }
}

.workspace-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
}

.workspace-items {
  display: flex;
  flex-direction: column;
}

.workspace-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 8px;
  border: none;
  background: none;
  border-radius: $border-radius-m;
  text-align: start;
  @include transition(background 150ms ease);

  &:hover {
    @include themed() {
      background: t($grey-03);
    }
  }
}

.workspace-initials {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  @extend %font-caption-medium;

  @include themed() {
    background-color: t($grey-10);
    color: t($grey-60);
  }
}

.workspace-name {
  @extend %font-body-medium;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;

  @include themed() {
    color: t($primary);
  }
}

.empty {
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-60);
  }
}
</style>
